import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProviderContext } from '../../../utils/context/ProviderContext.tsx';
import type { FiletredAllowedAccounts, ProviderFormState } from '../../types.ts';
import { ConfirmationDialogContent } from '../components/ConfirmationModalContent.tsx';
import { transformEditFormDataToApiPayload } from '../utils.ts';

export const useConfirmationModal = () => {
  const { t } = useTranslation();
  const { open, setLoading, close } = useConfirmationDialog();
  const { useEditProvider, useEditProviderAllowedAccounts } = useProviderContext();
  const { editProvider } = useEditProvider();
  const { linkOrganizationsToProvider, unlinkOrganizationsFromProvider } = useEditProviderAllowedAccounts();

  const handleOnConfirmed = useCallback(
    async (formValues: ProviderFormState, updatedAllowedAccounts: FiletredAllowedAccounts) => {
      setLoading(true);

      const payload = transformEditFormDataToApiPayload(formValues);
      const { accountsToLink, accountsToUnlink } = updatedAllowedAccounts;

      if (accountsToLink?.length) {
        await linkOrganizationsToProvider(accountsToLink);
      }

      if (accountsToUnlink?.length) {
        await unlinkOrganizationsFromProvider(accountsToUnlink);
      }

      editProvider(payload);
      setLoading(false);
      close();
    },
    [close, editProvider, linkOrganizationsToProvider, setLoading, unlinkOrganizationsFromProvider]
  );

  return useCallback(
    (
      providerName: string,
      formValues: ProviderFormState,
      touchedFields: string[],
      updatedAllowedAccounts: FiletredAllowedAccounts
    ) => {
      open({
        confirmationButtonColor: 'primary',
        confirmationDisabled: touchedFields.length === 0,
        confirmationLabel: t('Save Changes'),
        content: (
          <ConfirmationDialogContent
            formValues={formValues}
            providerName={providerName}
            touchedFields={touchedFields}
          />
        ),
        customContent: true,
        onConfirmed: () => handleOnConfirmed(formValues, updatedAllowedAccounts),
        title: t('Update Provider'),
      });
    },
    [handleOnConfirmed, open, t]
  );
};
